import React, {useCallback, useEffect, useRef, useState} from 'react';
import {isRight} from 'fp-ts/lib/Either';
import {tryAppleLogIn, tryGoogleLogIn} from '../../api/endpoints';
import {useAuthenticationContext} from '../../providers/AuthenticationProvider';
import './SocialLoginButtons.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID ?? '';
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI ?? '';

const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_SCRIPT_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

declare global {
    interface Window {
        google?: any;
        AppleID?: any;
    }
}

function loadScriptOnce(src: string): Promise<void> {
    if (typeof document === 'undefined') return Promise.resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
        if (existing.dataset.loaded === 'true') return Promise.resolve();
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve(), {once: true});
            existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {once: true});
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', () => {
            script.dataset.loaded = 'true';
            resolve();
        }, {once: true});
        script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {once: true});
        document.head.appendChild(script);
    });
}

interface SocialLoginButtonsProps {
    rememberMe?: boolean;
    onError?: (message: string) => void;
    compact?: boolean;
    dividerLabel?: string;
}

function extractErrorMessage(left: unknown, fallback: string): string {
    if (typeof left === 'string') return left;
    if (left && typeof left === 'object') {
        const message = (left as {message?: unknown}).message;
        if (typeof message === 'string' && message.trim() !== '') return message;
    }
    return fallback;
}

const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
    rememberMe = true,
    onError,
    compact = false,
    dividerLabel = 'or',
}) => {
    const {setLogin} = useAuthenticationContext();
    const googleButtonRef = useRef<HTMLDivElement | null>(null);
    const [appleReady, setAppleReady] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const reportError = useCallback((message: string) => {
        setErrorMessage(message);
        onError?.(message);
    }, [onError]);

    const handleGoogleCredential = useCallback(async (idToken: string) => {
        setErrorMessage(null);
        setIsProcessing(true);
        try {
            const result = await tryGoogleLogIn(idToken, rememberMe);
            if (isRight(result)) {
                setLogin(result.right);
            } else {
                reportError(extractErrorMessage(result.left, 'Could not sign in with Google. Please try again.'));
            }
        } finally {
            setIsProcessing(false);
        }
    }, [rememberMe, setLogin, reportError]);

    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;
        let cancelled = false;

        const buttonWidth = compact ? 240 : 320;

        loadScriptOnce(GOOGLE_SCRIPT_SRC).then(() => {
            if (cancelled || !window.google || !googleButtonRef.current) return;
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: (resp: {credential: string}) => {
                    if (resp?.credential) handleGoogleCredential(resp.credential);
                },
                ux_mode: 'popup',
                auto_select: false,
            });
            window.google.accounts.id.renderButton(googleButtonRef.current, {
                type: 'standard',
                theme: 'filled_blue',
                size: 'large',
                shape: 'rectangular',
                text: 'continue_with',
                logo_alignment: 'left',
                width: buttonWidth,
            });
        }).catch(() => reportError('Could not load Google sign-in. Check your internet connection and try again.'));

        return () => { cancelled = true; };
    }, [handleGoogleCredential, reportError, compact]);

    useEffect(() => {
        if (!APPLE_CLIENT_ID) return;
        let cancelled = false;

        loadScriptOnce(APPLE_SCRIPT_SRC).then(() => {
            if (cancelled || !window.AppleID) return;
            window.AppleID.auth.init({
                clientId: APPLE_CLIENT_ID,
                scope: 'name email',
                redirectURI: APPLE_REDIRECT_URI || window.location.origin,
                usePopup: true,
            });
            setAppleReady(true);
        }).catch(() => reportError('Could not load Apple sign-in. Check your internet connection and try again.'));

        return () => { cancelled = true; };
    }, [reportError]);

    const handleAppleClick = useCallback(async () => {
        if (!window.AppleID) return;
        setErrorMessage(null);
        try {
            const response = await window.AppleID.auth.signIn();
            const idToken: string | undefined = response?.authorization?.id_token;
            if (!idToken) {
                reportError('Apple sign-in did not return an ID token');
                return;
            }
            setIsProcessing(true);
            try {
                const userPayload = response?.user ? {name: response.user.name} : null;
                const result = await tryAppleLogIn(idToken, rememberMe, userPayload);
                if (isRight(result)) {
                    setLogin(result.right);
                } else {
                    reportError(extractErrorMessage(result.left, 'Could not sign in with Apple. Please try again.'));
                }
            } finally {
                setIsProcessing(false);
            }
        } catch (err: any) {
            if (err?.error === 'popup_closed_by_user') return;
            reportError('Could not sign in with Apple');
        }
    }, [rememberMe, setLogin, reportError]);

    if (!GOOGLE_CLIENT_ID && !APPLE_CLIENT_ID) return null;

    return (
        <div className={`social-login-wrapper${compact ? ' social-login-wrapper-compact' : ''}`}>
            <div className='social-login-divider' role='separator' aria-label={dividerLabel}>
                <span className='social-login-divider-line'/>
                <span className='social-login-divider-label'>{dividerLabel}</span>
                <span className='social-login-divider-line'/>
            </div>
            <div className='social-login-buttons'>
                {GOOGLE_CLIENT_ID && (
                    <div ref={googleButtonRef} className='social-login-google'/>
                )}
                {APPLE_CLIENT_ID && (
                    <button
                        type='button'
                        className={`social-login-apple${compact ? ' social-login-apple-compact' : ''}`}
                        onClick={handleAppleClick}
                        disabled={!appleReady || isProcessing}
                    >
                        <span className='social-login-apple-logo' aria-hidden='true'></span>
                        Continue with Apple
                    </button>
                )}
                {isProcessing && (
                    <div className='social-login-processing' role='status' aria-live='polite'>
                        <div className='loading-spinner social-login-processing-spinner'></div>
                        <div className='social-login-processing-text'>Signing you in…</div>
                    </div>
                )}
            </div>
            {errorMessage && (
                <div className='social-login-error' role='alert'>
                    <span className='social-login-error-icon' aria-hidden='true'>!</span>
                    <span className='social-login-error-text'>{errorMessage}</span>
                    <button
                        type='button'
                        className='social-login-error-close'
                        onClick={() => setErrorMessage(null)}
                        aria-label='Dismiss error'
                    >
                        &times;
                    </button>
                </div>
            )}
        </div>
    );
};

export default SocialLoginButtons;
